/**
 * THE ENGINE SEAT — a global override for which model class a sitting runs on.
 *
 * ── WHAT IT IS FOR (founder, 2026-08-27) ─────────────────────────────────────────────────────
 * Testing. The cost gap between the two classes is roughly 15× on a chat round, and the question
 * nobody has answered is whether the cheap seat can hold a character — comedy being the harshest
 * bench there is, since a comic character who wavers stops being funny instantly. This is the lever
 * that lets two sittings be compared without editing a pack or restarting anything.
 *
 * ⚠ IT IS NOT A PRICE, AND MUST NOT BECOME ONE WITHOUT A RULING. `doctrine-pricing-and-monetization.md`
 * forbids quality tiers by name — *"all visits use the same LLM at the same quality; no pay-more-for-
 * the-smart-model patterns"* — and the spec's §3.5 draws the operative line at **whether a chooser
 * is ever rendered to a paying player**. This chooser is admin-only and changes no price, so it sits
 * on the allowed side of that line. Widening it to everyone is the reversal, not a settings change.
 *
 * ⚠ AND IT IS NOT NAMED "QUALITY". The seats are named as the pipeline names them — Regular and
 * Heavy — because calling the default anything else would be the product asserting that its own
 * shipped experience is the lesser one, which is a claim nobody has evidence for and which the
 * bench exists to settle.
 *
 * Stored in localStorage, per browser, exactly as the locale is: a testing preference is not
 * account state, and an account column for it would be a migration in service of a dev tool.
 */

/** Vendor keys the server's `LlmClientFactory` accepts — see `Program.cs`. */
export type Seat = 'default' | 'regular' | 'heavy'

const KEY = 'heimar.seat'

/**
 * What each seat resolves to on the wire.
 *
 * ⚠ `default` SENDS NOTHING, which is the whole point: with no override the server falls back to
 * `Pack.HeavyConversation` (`CorrespondentStrategy.cs:88`), so an untouched setting is byte-for-byte
 * the behaviour that existed before this file. There is no "off" state to get wrong.
 */
const VENDOR: Record<Seat, string | undefined> = {
  default: undefined,
  regular: 'deepseek',
  heavy: 'anthropic',
}

export const SEATS: readonly Seat[] = ['default', 'regular', 'heavy'] as const

/** How the seat reads in the admin control. The model, because the model is what is being tested. */
export const SEAT_LABEL: Record<Seat, string> = {
  default: "the pack's own seat",
  regular: 'regular · deepseek',
  heavy: 'heavy · sonnet',
}

export function readSeat(): Seat {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'regular' || v === 'heavy' ? v : 'default'
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return 'default'
  }
}

export function writeSeat(seat: Seat) {
  try {
    if (seat === 'default') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, seat)
  } catch {
    /* a preference that cannot be stored is not worth failing over */
  }
}

/**
 * The override to send with `startSession`, or undefined for the pack's own seat.
 *
 * ⚠ FIXED AT SESSION CREATION AND NEVER AFTER, like the language. Switching seats mid-thread would
 * leave half a conversation written by one model and half by another, which is the one way to make
 * a comparison that proves nothing.
 */
export function seatOverride(): { chunkGenVendor?: string } {
  const vendor = VENDOR[readSeat()]
  return vendor ? { chunkGenVendor: vendor } : {}
}
